import React, {CSSProperties, memo, SyntheticEvent, useEffect, useRef, useState} from 'react'
import clsx from 'clsx';
import './styles.css';
import {ErrorTextWithBox, Icon, ListBox, Spinner, Text, ThemeProps} from "atmosfair-ui"
import {v4 as uuidv4} from 'uuid';
import {useTranslation} from "react-i18next";
import {AirlineResponse, AirportResponse} from "../../api/model/activity-flight";
import {
    GET_AIRLINES,
    GET_AIRPORTS,
    GET_CITIES,
    GET_COMPANIES,
    GET_COORDINATES_FROM_ADDRESS,
    GET_COUNTRIES,
    GET_CUSTOMERS,
    GET_HOTEL_NAMES
} from "../../api/endpoints";
import ErrorText from "../errors/error-text/ErrorText";
import {
    isAirlineResponse,
    isAirportResponse,
    isCityResponse,
    isCountryResponse,
    isCustomer,
    isCustomerCompanyResponse,
    isFormCustomer,
    isGeocodingFeature,
    isHotelResponse
} from "../../utils/check/type-guards";
import {useAutocomplete} from "@mui/base";
import {theme} from "../../styles/theme";
import Cookies from "js-cookie";
import {CityResponse, CountryResponse, HotelResponse} from "../../api/model/activity-hotel";
import {Customer, CustomerCompany, CustomerRequestType} from "../../api/model/customer";
import {COOKIE_NAME, VALIDATE_FORM_STARTING_NUMBER} from "../../constants/default-values";
import {generateAirportIataAirportNameAndCountryString} from "../../utils/string/flight";
import {CustomCustomerFormProps} from "../../components/forms/customer-form/CustomerForm";
import usePrevious from "../../hooks/use-previous";
import {isEqual} from "lodash";
import {GeocodingResponse} from "@mapbox/search-js-core/dist/geocode/GeocodingCore";
import {GeocodingFeature} from "@mapbox/search-js-core/dist/geocode/types";
import {SMALL_SPINNER_SIZE} from "../../utils/common";
import useFocus from '../../hooks/use-focus';
import {toast} from "react-toastify";
import ToastWithTextOnly from "../toasts/toast-with-text-only/ToastWithTextOnly";
import {constructRequiredLabelString} from "../../utils/string/common";

export enum AUTOCOMPLETE_ACTIVITY_TYPE {
    hotel,
    flight,
    customer,
    car_bus
}

export enum AUTOCOMPLETE_QUERY_TYPE {
    airline,
    airport,
    city,
    country,
    hotel,
    customerFirstName,
    customerLastName,
    customerEmail,
    customerCompany,
    freelyTypedAddress
}

export interface CommonAutocompleteInputProps {
    "aria-invalid"?: "true" | "false",
    "aria-required": "true"
    className: string,
    type: 'search'
    "aria-errormessage"?: string,
    placeholder: string,
    readOnly: boolean,
}

type ValueProps =
    AirlineResponse
    | AirportResponse
    | HotelResponse
    | CityResponse
    | CountryResponse
    | CustomCustomerFormProps
    | CustomerCompany
    | GeocodingFeature
    | null

type ValuePropsArray =
    AirlineResponse[]
    | AirportResponse[]
    | HotelResponse[]
    | CityResponse[]
    | CountryResponse[]
    | CustomCustomerFormProps[]
    | CustomerCompany[]
    | GeocodingFeature[]

export interface AutocompleteQueryProps {
    ariaErrorMessage?: string,
    placeholder: CommonAutocompleteInputProps["placeholder"],
    defaultValue?: ValueProps,
    setSelection: (selection: ValueProps) => void,
    inputClasses?: string,
    containerClasses?: string,
    error?: boolean,
    label: string,
    listBoxClasses?: string,
    containerStyle?: CSSProperties,
    required: boolean,
    queryType: AUTOCOMPLETE_QUERY_TYPE,
    labelColor?: keyof ThemeProps["colors"],
    type: AUTOCOMPLETE_ACTIVITY_TYPE,
    secondSearchParam?: CountryResponse | CityResponse | null,
    thirdSearchParam?: CountryResponse | null,
    disabled?: CommonAutocompleteInputProps["readOnly"],
    setFreelyTypedString?: (value: string) => void,
    getFreelyTypedString?: number,
    closeListBoxOnBlur?: boolean,
    agencyId?: number | null,
    onFocus?: () => void
    onBlur?: () => void
}

const MINIMUM_QUERY_LENGTH = 3
const MAPBOX_MAX_QUERY_LENGTH = 256

/**
 * This component implements a text input in which the user can freely type a string and gets suggestions based on the
 * freely typed input.
 * Things to consider: If the user has selecte sth from the list box, unfocusses the text input and focusses the text input again,
 * the list box will flash shortly and then disappear. To prevent this from happening make sure to pass a defaultValue with the
 * value of the current selection. The second option would be to compare the current string from the text input with the previous
 * string in it, but since this component will be used in the context of "defaultValue will always be passed because elements will
 * be loaded or edited", this is currently not implemented.
 */

const AutocompleteQuery = memo(function AutocompleteQuery({
                                                              agencyId,
                                                              ariaErrorMessage,
                                                              inputClasses,
                                                              containerClasses,
                                                              defaultValue,
                                                              error,
                                                              label,
                                                              placeholder,
                                                              listBoxClasses,
                                                              containerStyle,
                                                              setSelection,
                                                              required,
                                                              queryType,
                                                              labelColor,
                                                              type,
                                                              secondSearchParam,
                                                              thirdSearchParam,
                                                              disabled,
                                                              setFreelyTypedString,
                                                              getFreelyTypedString,
                                                              closeListBoxOnBlur,
                                                              onFocus,
                                                              onBlur,
                                                          }: AutocompleteQueryProps) {
    // selected value from list box
    const [value, setValue] = useState<ValueProps>(null);
    // freely typed value in text field
    const [inputValue, setInputValue] = useState('');
    // all values returned from API which are shown subsequently in list box
    const [queriedValues, setQueriedValues] = useState<ValuePropsArray>([]);
    const [focused, setFocused] = useState(false)
    const [networkError, setNetworkError] = useState(false);
    const [showListBoxClickAwayText, setShowListBoxClickAwayText] = useState(false)
    const [showInputClickAwayText, setShowInputClickAwayText] = useState(false)
    const [loading, setLoading] = useState(false)
    const {t, i18n} = useTranslation()
    const keyHasBeenPressed = useRef(false)
    const currentLanguage = i18n.resolvedLanguage
    const abortControllerRef = useRef<AbortController | null>(null)
    const previousDefaultValue = usePrevious(defaultValue)
    const {resolvedLanguage} = i18n
    const ariaForId = useRef(`:autocomplete-query-aria-for-id-${uuidv4()}:`)
    const {
        getRootProps,
        getInputLabelProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
    } = useAutocomplete({
        autoComplete: false,
        blurOnSelect: true,
        clearOnBlur: false,
        clearOnEscape: true,
        filterOptions: x => x,
        // sets the string in the input field when the user has selected something from the list box
        getOptionLabel: option => {
            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.flight && (isAirportResponse(option) || isAirlineResponse(option))) return `${option.name} (${option.iata})`
            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.hotel && (isHotelResponse(option) || isCityResponse(option) || isCountryResponse(option))) return option.name
            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.car_bus && isGeocodingFeature(option)) return `${option.properties.full_address}`
            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.customer && isCustomer(option)) {
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName) return option.firstName ?? ""
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName) return option.lastName ?? ""
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany) return option.company?.name ?? ""
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail) return option.email ?? ""
            }
            return ''
        },
        isOptionEqualToValue: (option, value) => {
            if (isFormCustomer(option) && isFormCustomer(value)) {
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany) return option.company?.id === value.company?.id
            }
            return option.id === value.id
        },
        readOnly: Boolean(disabled),
        id: ariaForId.current,
        includeInputInList: true,
        filterSelectedOptions: true,
        openOnFocus: false,
        options: queriedValues,
        // invoked whenever you select the display options in the list box.
        onChange: (event: SyntheticEvent, newValue: ValueProps) => _onChange(newValue),
        // invoked whenever you type in input field.
        onInputChange: (event, newInputValue: string) => {
            // by checking if event is null, we prevent setting value to null when its set via defaultValue
            if (event !== null) setValue(null)
            setShowListBoxClickAwayText(false)
            setShowInputClickAwayText(false)
            setLoading(false)
            _onInputChange(newInputValue, disabled)
        },
        value,
        inputValue,
        defaultValue
    })
    const isInputFocused = useFocus(ariaForId.current)

    useEffect(() => {
        if ((
                queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName ||
                queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail ||
                queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName ||
                queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany)
            && agencyId === undefined) throw new Error('You must pass an agency ID when AUTOCOMPLETE_QUERY_TYPE is set to customerFirstName, customerEmail, customerLastName or customerCompany.')
    }, [])

    useEffect(() => {
        if (defaultValue !== undefined && !isEqual(defaultValue, previousDefaultValue)) {
            setValue(defaultValue)
            if (defaultValue === null) setInputValue('')
        }
    }, [defaultValue])

    useEffect(() => {
        if (inputValue === '') {
        } else {
            setQueriedValues([])
            const trimmedInputValue = inputValue.trim()
            if (!disabled && trimmedInputValue.length > 0) {
                /**
                 * Executes the API request with the users input. Before talking to the API, it makes a few plausibility checks.
                 * The most important one is, it checks if the given default value is the same as the current string value in the text input.
                 * If that is the case, no API request will be made, which also means, the list box won't show up.
                 */
                switch (queryType) {
                    case AUTOCOMPLETE_QUERY_TYPE.airline:
                        if ((isAirlineResponse(defaultValue) && defaultValue.name !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined')) _getAirlines(trimmedInputValue)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.airport:
                        if ((isAirportResponse(defaultValue) && defaultValue.name !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined')) _getAirports(trimmedInputValue)
                        break
                    case AUTOCOMPLETE_QUERY_TYPE.city:
                        if ((isCityResponse(defaultValue) && defaultValue.name !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined')) _getCities(trimmedInputValue, secondSearchParam?.alpha2)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.country:
                        if ((isCountryResponse(defaultValue) && defaultValue.name !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined')) _getCountries(trimmedInputValue)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.hotel:
                        if ((isHotelResponse(defaultValue) && defaultValue.name !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined')) _getHotelNames(trimmedInputValue, secondSearchParam?.name, thirdSearchParam?.alpha2)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.customerFirstName:
                        if ((agencyId !== null && agencyId !== undefined) && ((isFormCustomer(defaultValue) && defaultValue.firstName !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined'))) _getCustomers(trimmedInputValue, CustomerRequestType.firstName, agencyId)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.customerLastName:
                        if ((agencyId !== null && agencyId !== undefined) && ((isFormCustomer(defaultValue) && defaultValue.lastName !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined'))) _getCustomers(trimmedInputValue, CustomerRequestType.lastName, agencyId)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.customerEmail:
                        if ((agencyId !== null && agencyId !== undefined) && ((isFormCustomer(defaultValue) && defaultValue.email !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined'))) _getCustomers(trimmedInputValue, CustomerRequestType.email, agencyId)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.customerCompany:
                        if ((agencyId !== null && agencyId !== undefined) && ((isFormCustomer(defaultValue) && defaultValue.company?.name !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined'))) _getCompany(trimmedInputValue, agencyId)
                        break;
                    case AUTOCOMPLETE_QUERY_TYPE.freelyTypedAddress:
                        if (
                            (
                                (isGeocodingFeature(defaultValue) && defaultValue.properties.full_address !== trimmedInputValue) || (defaultValue === null || typeof defaultValue === 'undefined')) &&
                            trimmedInputValue.length >= 3
                        ) {
                            // https://docs.mapbox.com/api/search/geocoding/#forward-geocoding-with-search-text-input
                            if (trimmedInputValue.length >= MAPBOX_MAX_QUERY_LENGTH) {
                                toast(<ToastWithTextOnly
                                        message={t("TOASTS.ERROR.MAPBOX_ADDRESS_TO_LONG", {maxLength: MAPBOX_MAX_QUERY_LENGTH})}/>,
                                    {progress: undefined, type: "error"}
                                )
                            } else {
                                _getAddress(trimmedInputValue)
                            }

                        }
                        break
                    default:
                        queryType satisfies never
                }
            }
        }
    }, [inputValue])

    /**
     * There might be the need to get the freely typed string from the text input. This useEffect provides this functionality.
     * Whenever getFreelyTypedString changes its value, it passes the string to the parent component. It handels the case
     * if string is the default value or a value typed by the user
     */
    useEffect(() => {
        if (setFreelyTypedString && typeof getFreelyTypedString === "number" && getFreelyTypedString > VALIDATE_FORM_STARTING_NUMBER) {
            let triggered = false
            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.customer && isFormCustomer(defaultValue)) {
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName && defaultValue.firstName !== inputValue) {
                    setFreelyTypedString(inputValue)
                    triggered = true
                }
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName && defaultValue.lastName !== inputValue) {
                    setFreelyTypedString(inputValue)
                    triggered = true
                }
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany && defaultValue.company?.name !== inputValue) {
                    setFreelyTypedString(inputValue)
                    triggered = true
                }
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail && defaultValue.email !== inputValue) {
                    setFreelyTypedString(inputValue)
                    triggered = true
                }
            }

            if (
                (type === AUTOCOMPLETE_ACTIVITY_TYPE.flight && (isAirportResponse(defaultValue) || isAirlineResponse(defaultValue))) &&
                defaultValue.name !== inputValue
            ) {
                setFreelyTypedString(inputValue)
                triggered = true
            }

            if (
                (type === AUTOCOMPLETE_ACTIVITY_TYPE.hotel && (isHotelResponse(defaultValue) || isCityResponse(defaultValue) || isCountryResponse(defaultValue))) &&
                defaultValue.name !== inputValue
            ) {
                setFreelyTypedString(inputValue)
                triggered = true
            }

            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.car_bus && isGeocodingFeature(defaultValue) && defaultValue.properties.full_address !== inputValue) {
                setFreelyTypedString(inputValue)
                triggered = true
            }


            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.customer && isFormCustomer(value) && !triggered) {
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName && value.firstName !== inputValue) setFreelyTypedString(inputValue)
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName && value.lastName !== inputValue) setFreelyTypedString(inputValue)
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany && value.company?.name !== inputValue) setFreelyTypedString(inputValue)
                if (queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail && value.email !== inputValue) setFreelyTypedString(inputValue)
            }

            if (
                (type === AUTOCOMPLETE_ACTIVITY_TYPE.flight && (isAirportResponse(value) || isAirlineResponse(value))) &&
                !triggered &&
                value.name !== inputValue
            ) setFreelyTypedString(inputValue)

            if (
                (type === AUTOCOMPLETE_ACTIVITY_TYPE.hotel && (isHotelResponse(value) || isCityResponse(value) || isCountryResponse(value))) &&
                !triggered &&
                value.name !== inputValue
            ) setFreelyTypedString(inputValue)

            if (type === AUTOCOMPLETE_ACTIVITY_TYPE.car_bus && isGeocodingFeature(value) && !triggered && value.properties.full_address !== inputValue)
                setFreelyTypedString(inputValue)
        }
    }, [getFreelyTypedString]);

    const _focusElementById = () => {
        const element = document.getElementById(ariaForId.current);
        element?.focus();
    };

    const _setHeaders = () => {
        let headers = new Headers();
        const token = Cookies.get(COOKIE_NAME)
        headers.set('Authorization', `Bearer ${token}`);
        return headers
    }

    const _getAddress = async (query: string) => {
        if (resolvedLanguage) {
            try {
                const response = await fetch(`${GET_COORDINATES_FROM_ADDRESS}?address=${query}&language=${resolvedLanguage}&limit=10`,
                    {
                        method: "GET",
                        headers: _setHeaders()
                    })
                if (response.ok) {
                    const addresses: GeocodingResponse = await response.json()
                    setQueriedValues(addresses.features)
                    setNetworkError(false)
                } else {
                    setNetworkError(true);
                    console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
                }
            } catch (error) {
                setNetworkError(true)
                console.error(`Something went wrong with getting addresses: ${error}`);
            }
        }
    }

    const _getCompany = async (query: string, agencyId: number) => {
        try {
            const response = await fetch(`${GET_COMPANIES}/${agencyId}?query=${query}`,
                {
                    method: "GET",
                    headers: _setHeaders()
                });

            if (response.ok) {
                const customers: CustomerCompany[] = await response.json();
                setQueriedValues(customers);
                setNetworkError(false)
            } else {
                setNetworkError(true);
                console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
            }
        } catch (error) {
            setNetworkError(true)
            console.error(`Something went wrong with getting customers companies: ${error}`);
        }
    }

    const _getCustomers = async (query: string, type: CustomerRequestType, agencyId: number) => {
        try {
            const response = await fetch(`${GET_CUSTOMERS}/${agencyId}/${type}?query=${query}`,
                {
                    method: "GET",
                    headers: _setHeaders()
                });

            if (response.ok) {
                const customers: Customer[] = await response.json();
                setQueriedValues(customers);
                setNetworkError(false)
            } else {
                setNetworkError(true);
                console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
            }
        } catch (error) {
            setNetworkError(true)
            console.error(`Something went wrong with getting customers: ${error}`);
        }
    }

    const _getHotelNames = async (query: string, city?: string | null, alpha2?: string | null) => {
        const transformedQuery = query.trim().replace(/\s+/g, ' ')
        if (abortControllerRef.current) abortControllerRef.current.abort(); // Abort the previous request if it exists
        abortControllerRef.current = new AbortController()
        const signal = abortControllerRef.current.signal
        let uri = `${GET_HOTEL_NAMES}`
        const params = [];
        if (city) {
            const transformedCityQuery = city.trim().replace(/\s+/g, ' ');
            params.push(`city=${transformedCityQuery}`);
        }
        if (alpha2) params.push(`alpha2=${alpha2}`);
        if (params.length > 0) uri = `${uri}?${params.join('&')}`;

        try {
            if (query.length > MINIMUM_QUERY_LENGTH) setLoading(true)
            const response = await fetch(uri,
                {
                    method: "POST",
                    headers: _setHeaders(),
                    body: transformedQuery,
                    signal
                });

            if (!signal.aborted) {
                if (response.ok) {
                    const hotels: HotelResponse[] = await response.json();
                    setQueriedValues(hotels);
                    setNetworkError(false)
                } else {
                    setNetworkError(true);
                    console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
                }
            }
        } catch (error) {
            if (!signal.aborted) {
                setNetworkError(true)
                console.error(`Something went wrong with getting hotel names: ${error}`);
            }
        } finally {
            if (!signal.aborted) setLoading(false)
        }
    }

    const _getCountries = async (query: string) => {
        const encodedQuery = encodeURIComponent(query);
        if (abortControllerRef.current) {
            abortControllerRef.current.abort(); // Abort the previous request if it exists
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        try {

            const response = await fetch(`${GET_COUNTRIES}?query=${encodedQuery}`,
                {
                    method: "GET",
                    headers: _setHeaders(),
                    signal
                });

            if (!signal.aborted) {
                if (response.ok) {
                    const countries: CountryResponse[] = await response.json();
                    setQueriedValues(countries);
                    setNetworkError(false)
                } else {
                    setNetworkError(true);
                    console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
                }
            }
        } catch (error) {
            if (!signal.aborted) {
                setNetworkError(true)
                console.error(`Something went wrong with getting airlines: ${error}`);
            }
        }
    }

    const _getCities = async (query: string, alpha2?: string | null) => {
        const transformedQuery = query.trim().replace(/\s+/g, ' ')
        if (abortControllerRef.current) {
            abortControllerRef.current.abort(); // Abort the previous request if it exists
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        let uri = `${GET_CITIES}?query=${transformedQuery}`
        if (alpha2) {
            uri = `${uri}&alpha2=${alpha2}`
        }
        try {
            setLoading(true)
            const response = await fetch(uri,
                {
                    method: "GET",
                    headers: _setHeaders(),
                    signal
                });
            if (!signal.aborted) {
                if (response.ok) {
                    const cities: CityResponse[] = await response.json();
                    setQueriedValues(cities);
                    setNetworkError(false)
                } else {
                    setNetworkError(true);
                    console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
                }
            }
        } catch (error) {
            if (!signal.aborted) {
                setNetworkError(true)
                console.error(`Something went wrong with getting airlines: ${error}`);
            }
        } finally {
            if (!signal.aborted) setLoading(false)
        }
    }

    const _getAirports = async (query: string) => {
        try {
            const response = await fetch(`${GET_AIRPORTS}?query=${query}`,
                {
                    method: "GET",
                    headers: _setHeaders()
                });

            if (response.ok) {
                const airports: AirportResponse[] = await response.json();
                setQueriedValues(airports);
                setNetworkError(false)
            } else {
                setNetworkError(true);
                console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
            }
        } catch (error) {
            setNetworkError(true)
            console.error(`Something went wrong with getting airports: ${error}`);
        }
    }

    const _getAirlines = async (query: string) => {
        try {
            const response = await fetch(`${GET_AIRLINES}?query=${query}`,
                {
                    method: "GET",
                    headers: _setHeaders()
                });

            if (response.ok) {
                const airlines: AirlineResponse[] = await response.json();
                setQueriedValues(airlines);
                setNetworkError(false)
            } else {
                setNetworkError(true);
                console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
            }
        } catch (error) {
            setNetworkError(true)
            console.error(`Something went wrong with getting airlines: ${error}`);
        }
    }

    const _onInputChange = (newInputValue: string, disabled: boolean | undefined) => {
        if (!Boolean(disabled)) setInputValue(newInputValue)
    }

    /**
     * Function invoked when user selects a value from the list box
     */
    const _onChange = (newValue: ValueProps) => {
        setShowListBoxClickAwayText(false)
        setShowInputClickAwayText(false)
        setQueriedValues([])
        setValue(newValue)
        setSelection(newValue)
        setInputValue('')
        _focusElementById()
    }

    const _clickAwayTextConditionallyToLowerText = (noun: string) => {
        if (currentLanguage === 'en') return noun.toLowerCase()
        return noun
    }

    /**
     * Set the text in the box that appears when there is an open list box, but the user clicks anywhere on the page
     * but in the list box (thus not selecting an option from the list box)
     */
    const _setClickAwayTextText = () => {
        if (inputValue.trim().length === 0) return null
        switch (queryType) {
            case (AUTOCOMPLETE_QUERY_TYPE.airport):
                return t(
                    'ERRORS.CLICK_AWAY_AUTOCOMPLETE_QUERY',
                    {value: `${t('GENERAL.EINEN')} ${_clickAwayTextConditionallyToLowerText(t('GENERAL.AIRPORT'))}`}
                )
            case (AUTOCOMPLETE_QUERY_TYPE.hotel):
                return t(
                    'ERRORS.CLICK_AWAY_AUTOCOMPLETE_QUERY',
                    {value: `${t('GENERAL.EIN')} ${_clickAwayTextConditionallyToLowerText(t('GENERAL.HOTEL'))}`}
                )
            case (AUTOCOMPLETE_QUERY_TYPE.airline):
                return t(
                    'ERRORS.CLICK_AWAY_AUTOCOMPLETE_QUERY',
                    {value: `${t('GENERAL.EINE')} ${_clickAwayTextConditionallyToLowerText(t('GENERAL.AIRLINE'))}`}
                )
            case (AUTOCOMPLETE_QUERY_TYPE.country):
                return t(
                    'ERRORS.CLICK_AWAY_AUTOCOMPLETE_QUERY',
                    {value: `${t('GENERAL.EIN')} ${_clickAwayTextConditionallyToLowerText(t('GENERAL.COUNTRY'))}`}
                )
            case (AUTOCOMPLETE_QUERY_TYPE.city):
                return t(
                    'ERRORS.CLICK_AWAY_AUTOCOMPLETE_QUERY',
                    {value: `${t('GENERAL.EINE')} ${_clickAwayTextConditionallyToLowerText(t('GENERAL.CITY'))}`}
                )
            case AUTOCOMPLETE_QUERY_TYPE.freelyTypedAddress:
                return null
            case AUTOCOMPLETE_QUERY_TYPE.customerFirstName:
                return null
            case AUTOCOMPLETE_QUERY_TYPE.customerLastName:
                return null
            case AUTOCOMPLETE_QUERY_TYPE.customerEmail:
                return null
            case AUTOCOMPLETE_QUERY_TYPE.customerCompany:
                return null
            default:
                queryType satisfies never;
        }
    }

    const _constructCostumerString = (customerOption: Customer) => {
        const {
            company,
            firstName,
            lastName,
            email
        } = customerOption

        return <div className={"autocomplete-query-customer-text-wrapper"}>
            {firstName && <Text
                weight={queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName ? "bold" : "regular"}>{firstName}</Text>}
            {lastName && <Text
                weight={queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName ? "bold" : "regular"}>{lastName}</Text>}
            {email &&
                <Text weight={queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail ? "bold" : "regular"}>{email}</Text>}
            {company && <Text
                weight={queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany ? "bold" : "regular"}>{`(${company.name})`}</Text>}
        </div>
    }

    /**
     * The values listed here must return null in _setClickAwayTextText()
     */
    const _onClickAway = () => {
        if (
            queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName ||
            queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName ||
            queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany ||
            queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail ||
            queryType === AUTOCOMPLETE_QUERY_TYPE.freelyTypedAddress
        ) setQueriedValues([])

        if (queryType === AUTOCOMPLETE_QUERY_TYPE.freelyTypedAddress) {
            setInputValue("")
            setValue(null)
            setSelection(null)
        }
    }

    const _renderListBox = () => {
        const composedListBoxClasses = clsx(
            'input-field-result-list',
            'input-field-text-result-list-width',
            listBoxClasses
        )

        if (networkError && focused) return <ListBox
            unorderedListClasses={composedListBoxClasses}
            error={true}
            //@ts-ignore
            getProps={getListboxProps}
            hasLabel={true}>
            <ErrorText>ERRORS.GENERAL_NETWORK_ERROR_BODY</ErrorText>
        </ListBox>

        if (groupedOptions.length > 0) return <ListBox
            unorderedListClasses={composedListBoxClasses}
            hasLabel={true}
            onClickAway={_onClickAway}
            showClickAwayText={showListBoxClickAwayText}
            clickAwayText={_setClickAwayTextText()}
            //@ts-ignore
            getProps={getListboxProps}>
            {(groupedOptions as typeof queriedValues).map((option, index) => {
                if (isAirlineResponse(option) && queryType === AUTOCOMPLETE_QUERY_TYPE.airline) {
                    const airlineOption = option as AirlineResponse
                    return <li
                        role={'option'}
                        aria-selected={!!(value && isAirlineResponse(value) && value.iata === airlineOption.iata)}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {`${airlineOption.iata} - ${airlineOption.name}`}
                    </li>
                }
                if (isAirportResponse(option) && queryType === AUTOCOMPLETE_QUERY_TYPE.airport) {
                    const airportOption = option as AirportResponse;
                    return <li
                        role={'option'}
                        aria-selected={!!(value && isAirportResponse(value) && value.iata === airportOption.iata)}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {generateAirportIataAirportNameAndCountryString(airportOption)}
                    </li>
                }
                if (isHotelResponse(option) && queryType === AUTOCOMPLETE_QUERY_TYPE.hotel) {
                    const hotelOption = option as HotelResponse
                    return <li
                        role={'option'}
                        aria-selected={!!(value && isHotelResponse(value) && value.name === hotelOption.name)}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {`${hotelOption.name} - ${hotelOption.city}${hotelOption.region ? ` - ${hotelOption.region}` : ''}`}

                    </li>
                }
                if (isCityResponse(option) && queryType === AUTOCOMPLETE_QUERY_TYPE.city) {
                    const cityOption = option as CityResponse;
                    return <li
                        role={'option'}
                        aria-selected={!!(value && isCityResponse(value) && value.name === cityOption.name)}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {`${cityOption.name}${cityOption.region ? ` - ${cityOption.region}` : ''} - ${cityOption.alpha2}`}
                    </li>
                }

                if (isCountryResponse(option) && queryType === AUTOCOMPLETE_QUERY_TYPE.country) {
                    const countryOption = option as CountryResponse;
                    return <li
                        role={'option'}
                        aria-selected={!!(value && isCountryResponse(value) && value.name === countryOption.name)}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {`${countryOption.name} - ${countryOption.alpha2}`}
                    </li>
                }

                if (isFormCustomer(option) &&
                    (
                        queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName ||
                        queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName ||
                        queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail
                    )) {
                    const customerOption = option as Customer
                    return <li
                        role={'option'}
                        aria-selected={!!(value &&
                            (
                                queryType === AUTOCOMPLETE_QUERY_TYPE.customerFirstName && isFormCustomer(value) && value.firstName === customerOption.firstName ||
                                queryType === AUTOCOMPLETE_QUERY_TYPE.customerLastName && isFormCustomer(value) && value.lastName === customerOption.lastName ||
                                queryType === AUTOCOMPLETE_QUERY_TYPE.customerEmail && isFormCustomer(value) && value.email === customerOption.email
                            )
                        )}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {_constructCostumerString(customerOption)}
                    </li>
                }

                if (isCustomerCompanyResponse(option) && queryType === AUTOCOMPLETE_QUERY_TYPE.customerCompany) {
                    const customerCompanyOption = option as CustomerCompany
                    return <li
                        role={'option'}
                        aria-selected={!!(value && isCustomerCompanyResponse(value) && value.name === customerCompanyOption.name)}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {customerCompanyOption.name}
                    </li>
                }

                if (isGeocodingFeature(option) && queryType === AUTOCOMPLETE_QUERY_TYPE.freelyTypedAddress) {
                    const addressOption = option as GeocodingFeature
                    return <li
                        role={'option'}
                        aria-selected={!!(value && isGeocodingFeature(value) && value.id === addressOption.id)}
                        {...getOptionProps({option, index})}
                        key={index}>
                        {addressOption.properties.full_address}
                    </li>
                }

                return null
            })}
        </ListBox>
        return null
    }

    const _renderEndAdornment = () => {
        const val = defaultValue || value;
        if (isCityResponse(val) && val.region) return <p className={'end-adornment'}>({val?.region})</p>
        return null
    }

    const _calculateError = () => {
        if (error) return true
        return error ? !value : false
    }

    const _setLabelColor = () => {
        const {colors} = theme
        if (isInputFocused || groupedOptions.length > 0) return {color: colors.black}
        if (_calculateError()) return {color: colors.warningMain}
        if (labelColor) return {color: colors[labelColor]}
        return {color: colors.black}
    }

    const _onBlur = () => {
        onBlur && onBlur()
        if (closeListBoxOnBlur) {
            setQueriedValues([])
            setShowInputClickAwayText(false)
            setShowListBoxClickAwayText(false)
        } else if (value === null && keyHasBeenPressed.current) {
            if (groupedOptions.length === 0) {
                setShowInputClickAwayText(false)
            } else if (groupedOptions.length > 0) {
                setShowListBoxClickAwayText(true)
            } else {
                setShowInputClickAwayText(false)
                setShowListBoxClickAwayText(false)
            }
        } else {
            setShowInputClickAwayText(false)
            setShowListBoxClickAwayText(false)
        }
        setFocused(false)
        if (
            (inputValue.length === 0 || inputValue === null) &&
            defaultValue !== undefined
        ) setSelection(null)
    }

    const _renderInput = () => {
        const labelTranslated = t(label)
        const labelText = required ? constructRequiredLabelString(labelTranslated) : labelTranslated
        const commonProps: CommonAutocompleteInputProps = {
            "aria-required": "true",
            className: clsx(
                'input-field',
                groupedOptions.length > 0 && 'input-field-focus',
                _calculateError() && 'input-error',
                inputClasses,
                disabled && 'input-disabled'),
            type: 'search',
            placeholder: t(placeholder),
            readOnly: Boolean(disabled),
        }

        if (networkError || error) {
            commonProps["aria-invalid"] = "true"
            if (error && ariaErrorMessage) commonProps["aria-errormessage"] = t(ariaErrorMessage)
            if (networkError) commonProps["aria-errormessage"] = t("ERRORS.GENERAL_NETWORK_ERROR_BODY")
        }

        return <>
            <label
                htmlFor={ariaForId.current}
                className={clsx('base-text-styles label-text-styles', disabled && "autocomplete-query-customer-label-disabled")}
                style={_setLabelColor()}
                {...getInputLabelProps()}>
                {labelText}
            </label>
            <div className={"autocomplete-query-input-container"}>
                <input
                    style={{paddingRight: "25px"}}
                    {...commonProps}
                    {...getInputProps()}
                    onKeyDown={() => keyHasBeenPressed.current = true}
                    onFocus={() => {
                        setFocused(true)
                        onFocus && onFocus()
                    }}
                    onBlur={_onBlur}/>
                {loading && <Spinner
                    style={{position: "absolute", right: "10px", top: '25%'}}
                    size={SMALL_SPINNER_SIZE}
                    fullPage={false}/>}
                {_renderEndAdornment()}
            </div>
            {showInputClickAwayText && !loading &&
                <ErrorTextWithBox
                    classes={'autocomplete-query-error-text-with-box'}>
                    {_setClickAwayTextText()}
                </ErrorTextWithBox>}
        </>
    }

    return <div
        {...getRootProps()}
        className={clsx('autocomplete-query-input-container', disabled && 'autocomplete-query-disable-input-hover', containerClasses)}
        style={containerStyle}>
        {_renderInput()}
        {value !== null && inputValue.length > 0 && <Icon
            icon={'deleteLeft'}
            iconStyle={{fontSize: 19}}
            iconClasses={'input-delete-icon'}
            onClick={() => {
                setSelection(null)
                setInputValue('')
                setValue(null)
            }}/>}
        {_renderListBox()}
    </div>
})

export default AutocompleteQuery

